/**
 * Durable offline write queue.
 *
 * Mutations that fail because the device is offline are stored in IndexedDB and
 * replayed, in order, once connectivity returns — so an expert can keep filling
 * in a report (and shooting photos) on a site with no signal.
 *
 * Design notes:
 *  - Entries are *typed operations* (`{op, args}`), not raw requests: replay is
 *    explicit, and the queue can be unit-tested without a network layer.
 *  - Consecutive edits to the same target are COALESCED. The wizard autosaves on
 *    a debounce, so an offline hour would otherwise pile up hundreds of PATCHes;
 *    merging them keeps the queue small and replay fast (last write wins).
 *  - Only genuine network failures are queued. An HTTP error means the server
 *    was reached and answered — that is a real error, not an offline write.
 */

const DB_NAME = 'expertisr-offline';
const DB_VERSION = 1;
const STORE = 'queue';

/* ------------------------------------------------------------ storage ----- */

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/** IndexedDB-backed store. Swappable for an in-memory one in tests. */
export const indexedDbStore = {
  async all() {
    const db = await openDb();
    const items = await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readonly');
      const req = t.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return items.sort((a, b) => a.id - b.id);
  },
  async put(entry) {
    const db = await openDb();
    const id = await tx(db, 'readwrite', (store) => store.put(entry));
    db.close();
    return id;
  },
  async remove(id) {
    const db = await openDb();
    await tx(db, 'readwrite', (store) => store.delete(id));
    db.close();
  },
  async clear() {
    const db = await openDb();
    await tx(db, 'readwrite', (store) => store.clear());
    db.close();
  },
};

/** In-memory store — used by unit tests (jsdom has no IndexedDB). */
export function createMemoryStore() {
  let seq = 0;
  const items = new Map();
  return {
    async all() { return [...items.values()].sort((a, b) => a.id - b.id); },
    async put(entry) {
      const id = entry.id != null ? entry.id : (seq += 1);
      items.set(id, { ...entry, id });
      return id;
    },
    async remove(id) { items.delete(id); },
    async clear() { items.clear(); },
  };
}

/* -------------------------------------------------------------- queue ----- */

/** Operations whose payloads can be merged when they target the same entity. */
const COALESCING_OPS = new Set(['updateReport', 'updateStep', 'updateChecklistItem', 'updateImage']);

/** Identity of an operation's target, used to decide what can be merged. */
export function mergeKeyFor(entry) {
  if (!COALESCING_OPS.has(entry.op)) return null;
  // All coalescing ops take their ids first and the payload last.
  const ids = entry.args.slice(0, -1).join('/');
  return `${entry.op}:${ids}`;
}

export class OfflineQueue {
  constructor(store = indexedDbStore) {
    this.store = store;
    this.listeners = new Set();
    this.flushing = false;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async notify() {
    const count = await this.count();
    this.listeners.forEach((fn) => {
      try { fn(count); } catch { /* a bad listener must not break the queue */ }
    });
  }

  async count() {
    return (await this.store.all()).length;
  }

  async pending() {
    return this.store.all();
  }

  /** Queue one operation, merging it into a pending one when possible. */
  async enqueue(op, args) {
    const entry = { op, args, ts: Date.now(), attempts: 0 };
    const key = mergeKeyFor(entry);
    if (key) {
      const existing = (await this.store.all()).find((e) => mergeKeyFor(e) === key);
      if (existing) {
        const mergedPayload = { ...existing.args[existing.args.length - 1], ...args[args.length - 1] };
        const merged = { ...existing, args: [...args.slice(0, -1), mergedPayload], ts: Date.now() };
        await this.store.put(merged);
        await this.notify();
        return merged;
      }
    }
    await this.store.put(entry);
    await this.notify();
    return entry;
  }

  async clear() {
    await this.store.clear();
    await this.notify();
  }

  /**
   * Replay pending operations in order.
   *
   * `executors` maps an op name to an async function. Stops at the first
   * network failure (still offline) and keeps the remaining entries; drops an
   * entry the server rejected permanently (4xx) since replaying cannot help.
   */
  async flush(executors) {
    if (this.flushing) return { sent: 0, dropped: 0, stopped: false };
    this.flushing = true;
    let sent = 0;
    let dropped = 0;
    let stopped = false;
    try {
      for (const entry of await this.store.all()) {
        const run = executors[entry.op];
        if (!run) { await this.store.remove(entry.id); dropped += 1; continue; }
        try {
          await run(...entry.args);
          await this.store.remove(entry.id);
          sent += 1;
        } catch (err) {
          const status = err && err.response && err.response.status;
          if (status && status >= 400 && status < 500) {
            await this.store.remove(entry.id); // permanent: never replayable
            dropped += 1;
          } else {
            stopped = true; // offline or server-side issue: retry later, in order
            break;
          }
        }
      }
    } finally {
      this.flushing = false;
      await this.notify();
    }
    return { sent, dropped, stopped };
  }
}

/** True when a failure means "no network", as opposed to a server answer. */
export function isOfflineError(err) {
  if (!navigator.onLine) return true;
  return Boolean(err) && !err.response;
}

export const offlineQueue = new OfflineQueue();
